import moment from "moment"

import { Paginator } from "~/@shared/api-types/Paginator"
import { NewGradJobDetail as NewGradJobDetail__new } from "~/api/new-grad-jobs/[id]/get"
import type { NewGradJob as NewGradJob__new } from "~/api/new-grad-jobs/_types"
import { RecruitmentStatus } from "~/consts/index"

import { Company, CompanyForNewGradJobDetail, CreateCompanyArgs } from "./Company"
import { MajorOccupation, MinorOccupation } from "./MasterData"

export interface createNewGradJobArgs {
  id: number
  title: string
  job_type: string
  body_copy: string
  content: string
  qualification: string
  required_abilities: string
  selection_flow: string
  expected_recruitment_numbers?: string
  assumed_annual_income: string
  entry_is_open_year_round: boolean
  entry_starts_on: string
  entry_ends_on?: string
  assignment: string
  enter_period?: string
  needs_external_entry: boolean
  entry_url: string
  thumbnail_img_url: string
  status: keyof typeof RecruitmentStatus
  url: string
  limited_url_token: string
  created_at: string
  company_overview: string
  company_culture: string
  company: CreateCompanyArgs

  information_technology_skills?: { display: string }[]
  name: string
  display: string
  image?: { img_url: string }
  services: string
  mission: string
  job_description: string
  feature?: string
  career_path: string
  expected_experiences: string
  desired_experiences: string
  location: string
  salary: string
  employment_status: string
  employee_benefits: string
  work_pattern: string
  prefecture?: {
    display: string
  }
  images?: { img_url: string }[]
  coworkers?: {
    display: string
    introduction: string
    relationship: string
    img_url: string
  }[]
}

export class NewGradJob {
  id: number
  title: string
  jobType: string
  bodyCopy: string
  content: string
  qualification: string
  requiredAbilities: string
  selectionFlow: string
  expectedRecruitmentNumbers?: string
  assumedAnnualIncome: string
  entryIsOpenYearRound: boolean
  entryStartsOn: string | null
  entryEndsOn?: string | null
  assignment: string
  enterPeriod?: string
  isExternalApplication: boolean
  applicationUrl: string
  status: keyof typeof RecruitmentStatus
  url: string
  limitedUrlToken: string
  createdAt: string
  companyOverview: string
  companyCulture: string
  company: Company

  informationTechnologySkills?: { display: string }[]
  name: string
  jobCategory: string
  thumbnailImgUrl?: string
  prefecture?: string
  services: string
  mission: string
  jobDescription: string
  feature?: string
  careerPath: string
  expectedExperiences: string
  desiredExperiences: string
  location: string
  salary: string
  employmentStatus: string
  employeeBenefits: string
  workPattern: string
  imgPaths?: { imgPath: string }[]
  coworkers?: {
    name: string
    introduction: string
    relationship: string
    imgPath: string
  }[]

  constructor(args: createNewGradJobArgs) {
    this.id = args.id
    this.title = args.title
    this.jobType = args.job_type
    this.bodyCopy = args.body_copy
    this.content = args.content
    this.qualification = args.qualification
    this.requiredAbilities = args.required_abilities
    this.selectionFlow = args.selection_flow
    this.expectedRecruitmentNumbers = args.expected_recruitment_numbers
    this.assumedAnnualIncome = args.assumed_annual_income
    this.entryIsOpenYearRound = args.entry_is_open_year_round
    this.entryStartsOn = args.entry_starts_on ? moment(args.entry_starts_on).format("YYYY/MM/DD") : null
    this.entryEndsOn = args.entry_ends_on ? moment(args.entry_ends_on).format("YYYY/MM/DD") : null
    this.assignment = args.assignment
    this.enterPeriod = args.enter_period
    this.isExternalApplication = args.needs_external_entry
    this.applicationUrl = args.entry_url
    this.status = args.status
    this.url = args.url || ""
    this.limitedUrlToken = args.limited_url_token
    this.createdAt = args.created_at
    this.companyOverview = args.company_overview
    this.companyCulture = args.company_culture
    this.company = new Company(args.company)

    if (args.information_technology_skills) {
      this.informationTechnologySkills = args.information_technology_skills
    }
    this.name = args.name
    this.thumbnailImgUrl = args.thumbnail_img_url
    if (args.image) {
      this.thumbnailImgUrl = args.image.img_url
    }
    this.jobCategory = args.display
    if (args.prefecture) {
      this.prefecture = args.prefecture.display
    }
    this.services = args.services
    this.mission = args.mission
    this.jobDescription = args.job_description
    this.feature = args.feature
    this.careerPath = args.career_path
    this.expectedExperiences = args.expected_experiences
    this.desiredExperiences = args.desired_experiences
    this.location = args.location
    this.salary = args.salary
    this.employmentStatus = args.employment_status
    this.employeeBenefits = args.employee_benefits
    this.workPattern = args.work_pattern
    if (args.images) {
      this.imgPaths = args.images.map((image) => {
        return {
          imgPath: image.img_url,
        }
      })
    }
    if (args.coworkers) {
      this.coworkers = args.coworkers.map((coworker) => {
        return {
          name: coworker.display,
          introduction: coworker.introduction,
          relationship: coworker.relationship,
          imgPath: coworker.img_url,
        }
      })
    }
  }
}

/**
 * api/new-grad-jobs/groupedのjsonの["data"]の型
 * @deprecated {@link NewGradJob__new} ←これを使う
 */
export interface NewGradJobJson {
  original_id: number
  title: string
  major_occupation_name: string
  minor_occupation_name: string
  assumed_annual_income: number
  // thumbnail_img_path: string
  thumbnail_img_url: string
  curated: boolean
  rich: boolean
  company: {
    name: string
    logo_img_url: string
  }
}
/** api/new-grad-jobs/groupedのjsonの型 */
export interface GroupedRecruitment {
  id: number
  name: string
  display: string
  data: Array<NewGradJobJson>
}

// export type NewGradJobsReq = Paginator<NewGradJobJson>

// export interface NewGradJobsReq {
//   data: Array<NewGradJobJson>
//   links: {
//     first?: string
//     last?: string
//     prev?: string
//     next?: string
//   }
//   meta: PageInfoJson
// }

// export interface PageInfoJson {
//   current_page: number
//   from: number
//   last_page: number
//   path: string
//   per_page: number
//   to: number
//   total: number
// }

/** @deprecated {@link NewGradJobDetail__new} ←これを使う*/
export interface NewGradJobDetail {
  id: number
  title: string
  assumed_annual_income: number
  mission: string
  services: string
  job_description: string
  career_path: string
  qualification: string
  salary: string
  employee_benefits: string
  work_pattern: string
  location: string
  entry_period: string
  enter_period: string
  selection_flow: string
  needs_external_entry: boolean
  entry_url: string
  public: boolean
  images: { img_url: string }[]
  company: CompanyForNewGradJobDetail
  major_occupation: MajorOccupation
  minor_occupation: MinorOccupation
  regions: string[]
  target_grad_years: (number | null)[]
  salaries: { year: number; amount: number }[]
  work_schedules: WorkSchedule[]
  careers: Career[]
}

interface WorkSchedule {
  time: string
  title: string
  description: string
  img_url?: string
}

interface Career {
  title: string
  description: string
  steps: { title: string; enrollment_period: string }[]
}
